import { captureException } from '@sentry/react';
import is from '@sindresorhus/is';
import { GroupId } from '@sr/types';
import { useState } from 'react';
import Markdown from '../shared/Markdown';
import RuleExamples from '../shared/RuleExamples';
import ShowMore from '../shared/ShowMore';
import Upsell from './Upsell';
import { useActiveMessageGatedByPro } from 'store/selectors/message';
import { Box } from 'v1/ui';

export default function MessageDetails({ groupId }: { groupId: GroupId }) {
  const message = useActiveMessageGatedByPro();
  const [showMore, setShowMore] = useState(false);
  const hasReason = message && is.string(message.reason);

  // Show an upsell when a free user can't view a pro only message
  if (message === false) {
    return <Upsell groupId={groupId} />;
  }

  // There should always be an active message here
  if (message === undefined) {
    captureException(new Error('Missing active message'), {
      tags: {
        groupId,
        noFeedback: true,
      },
      fingerprint: ['missing-active-message'],
    });
    return null;
  }

  return (
    <Box>
      <Box sx={{ fontSize: 15, fontWeight: 600, color: 'black60', mb: 4 }}>
        {message.ruleInfo.title}
      </Box>
      {hasReason ? (
        <>
          <Markdown markdown={message.reason} />
          {showMore && (
            <>
              <Markdown markdown={message.ruleInfo.note} />
              <RuleExamples examples={message.ruleInfo.examples} />
            </>
          )}
          {message.ruleInfo.note || message.ruleInfo.examples ? (
            <ShowMore
              groupId={message.ruleInfo.groupId}
              ruleTitle={message.ruleInfo.title}
              showMore={showMore}
              setShowMore={setShowMore}
            />
          ) : null}
        </>
      ) : (
        <>
          <Markdown markdown={message.ruleInfo.note} />
          {showMore && <RuleExamples examples={message.ruleInfo.examples} />}
          {message.ruleInfo.examples ? (
            <ShowMore
              groupId={message.ruleInfo.groupId}
              ruleTitle={message.ruleInfo.title}
              showMore={showMore}
              setShowMore={setShowMore}
            />
          ) : null}
        </>
      )}
    </Box>
  );
}
